import { useEffect, useState } from 'react';
import * as M from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useRowSelection } from '../../../../../shared/hook/useRowSelection';
import type { Device, IDeviceFilterForm } from '../../../../../shared/types/device';
import { cleanParams } from '../../../../../shared/utils/cleanParams';
import * as C from '../../../../ui/common/CommonStyles';
import DeviceRegister from './ui/deviceRegister';
import DeviceTable from './ui/deviceTable';
import { DeviceExcelRegModal } from './ui/deviceExcelRegModal';
import DeviceFilterSection from './ui/filterSection';
import DeviceSummary from './ui/summary';
import useGetDevices from './hooks/useGetDevices';

const parseServices = (value: string | null): string[] => {
  if (!value) {
    return [];
  }

  return value
    .split(',')
    .map((service) => service.trim())
    .filter(Boolean);
};

const initialFilterForm: IDeviceFilterForm = {
  services: [],
  startDate: null,
  endDate: null,
  keyword: '',
};

export default function InternalContainer() {
  // const [modlinkInternalList, setModlinkInternalList] = useState<Device[]>([]);
  const [opened, { open, close }] = useDisclosure(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterForm, setFilterForm] = useState<IDeviceFilterForm>(initialFilterForm);

  const servicesParam = searchParams.get('services') || searchParams.get('service');
  const startDate = searchParams.get('serviceExpStartDt') || '';
  const endDate = searchParams.get('serviceExpEndDt') || '';
  const keyword = searchParams.get('keyword') || '';
  const searchWord = searchParams.get('keyword') || '';
  const shipped = searchParams.get('shipped') || 'all';
  const model = searchParams.get('model') || '';
  const subType = searchParams.get('subType') || '';
  const networkType = searchParams.get('networkType') || '';
  const services = parseServices(servicesParam);
  const page = searchParams.get('page') || '1';
  const size = searchParams.get('size') || '10';

  const data = {
    page: Number(page),
    size: Number(size),
    filter: {
      isShippedYn: shipped === 'all' ? '' : shipped,
      deviceCd: model,
      deviceSubCd: subType,
      networkType,
    },
    services,
    progix: services.includes('progix'),
    scautr: services.includes('scautr'),
    mfm: services.includes('MFM'),
    serviceExpStartDt: startDate,
    serviceExpEndDt: endDate,
    keyword: searchWord,
  };

  const { data: modlinkList } = useGetDevices(data);

  const {
    selectedIds: checkBoxArr,
    isAllSelected,
    toggleItem,
    toggleAll,
    resetSelection: resetCheckBoxArr,
  } = useRowSelection<Device, string>({
    items: modlinkList?.data || [],
    getId: (item) => item.mac,
    isSelectable: (item) => !item.shipment,
  });

  const updateFilterForm = (nextFilter: Partial<IDeviceFilterForm>) => {
    setFilterForm((prev) => {
      const mergedFilter = {
        ...prev,
        ...nextFilter,
      };

      if (mergedFilter.startDate && mergedFilter.endDate && dayjs(mergedFilter.endDate).isBefore(dayjs(mergedFilter.startDate), 'day')) {
        void Swal.fire({
          icon: 'error',
          title: '날짜 선택 오류',
          text: '종료일은 시작일보다 빠를 수 없습니다.',
          confirmButtonText: '확인',
        });

        return prev;
      }

      return mergedFilter;
    });
  };

  const applyFilters = (nextFilter: IDeviceFilterForm) => {
    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        service: '',
        startDate: '',
        endDate: '',
        services: nextFilter.services.join(','),
        serviceExpStartDt: nextFilter.startDate ? dayjs(nextFilter.startDate).startOf('day').toISOString().substring(0, 19) : '',
        serviceExpEndDt: nextFilter.endDate ? dayjs(nextFilter.endDate).endOf('day').toISOString().substring(0, 19) : '',
        keyword: nextFilter.keyword,
      }),
    );
  };

  const resetFilters = () => {
    setFilterForm(initialFilterForm);

    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        service: '',
        services: '',
        startDate: '',
        serviceExpStartDt: '',
        endDate: '',
        serviceExpEndDt: '',
        keyword: '',
      }),
    );
  };

  const onClickExpiredSoonSummary = () => {
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 14);

    const nextFilter = {
      ...filterForm,
      startDate,
      endDate,
    };

    setFilterForm(nextFilter);
    applyFilters(nextFilter);
  };

  useEffect(() => {
    setFilterForm({
      services: parseServices(servicesParam),
      startDate: startDate ? new Date(`${startDate}Z`) : null,
      endDate: endDate ? new Date(`${endDate}Z`) : null,
      keyword,
    });
  }, [servicesParam, startDate, endDate, keyword]);

  return (
    <div>
      <C.TitleBox>
        <div className="title">디바이스 관리</div>
      </C.TitleBox>
      <C.ListBox>
        <DeviceSummary onClickExpiredSoonSummary={onClickExpiredSoonSummary} />
        <DeviceFilterSection
          filterForm={filterForm}
          onChangeFilterForm={updateFilterForm}
          onSearch={() => applyFilters(filterForm)}
          onReset={resetFilters}
        />
        <M.Space h={20} />
        <M.Group justify="space-between" align="flex-end" mb="sm">
          {/* <SearchControls
            onChangeIsShippedParams={onChangeIsShippedParams}
            onChangeDeviceParams={onChangeDeviceParams}
            onChangeNetworkTypeParams={onChangeNetworkTypeParams}
          /> */}
          <M.Text fw={600}>디바이스 목록</M.Text>
          <DeviceRegister open={open} resetCheckBoxArr={resetCheckBoxArr} checkBoxArr={checkBoxArr} />
        </M.Group>
        <DeviceTable
          checkBoxArr={checkBoxArr}
          isAllChecked={isAllSelected}
          modlinkInternalList={modlinkList?.data || []}
          totalCount={modlinkList?.total || 0}
          onClickCheckBox={(e) => toggleItem(e.target.id, e.target.checked)}
          onClickCheckBoxAll={(e) => toggleAll(e.target.checked)}
        />
      </C.ListBox>
      <DeviceExcelRegModal opened={opened} close={close} header="모드링크 출고등록" />
    </div>
  );
}
